import React, { useState } from 'react';
import {
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    // Grid,
    Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

const styles = {
    container: {
        maxWidth: '900px',
        margin: '0 auto',
        padding: '2rem',
        fontFamily: "'Montserrat', sans-serif"
    },
    header: {
        textAlign: 'center',
        marginBottom: '3rem'
    },
    title: {
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 700,
        fontSize: '2.5rem',
        color: '#1C1B1F',
        marginBottom: '1rem'
    },
    subtitle: {
        fontFamily: "'Montserrat', sans-serif",
        fontSize: '1.1rem',
        color: '#49454F',
        maxWidth: '600px',
        margin: '0 auto'
    },
    section: {
        marginBottom: '3rem'
    },
    sectionTitle: {
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 600,
        fontSize: '1.5rem',
        color: '#1C1B1F',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
    },
    accordion: {
        marginBottom: '0.75rem',
        borderRadius: '12px !important',
        border: '1px solid #E0E0E0',
        boxShadow: 'none',
        '&:before': {
            display: 'none'
        },
        '&.Mui-expanded': {
            margin: '0 0 0.75rem 0',
            borderColor: '#E8DEF8'
        }
    },
    accordionSummary: {
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 500,
        fontSize: '1rem',
        color: '#1C1B1F',
        '&.Mui-expanded': {
            color: '#6750A4'
        }
    },
    accordionDetails: {
        fontFamily: "'Montserrat', sans-serif",
        fontSize: '0.95rem',
        color: '#49454F',
        lineHeight: 1.7,
        paddingTop: 0
    },
    contactCard: {
        padding: '2rem',
        borderRadius: '16px',
        border: '1px solid #E8DEF8',
        backgroundColor: '#F7F2F9',
        textAlign: 'center'
    },
    emailButton: {
        backgroundColor: '#6750A4',
        color: 'white',
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 600,
        fontSize: '1rem',
        padding: '0.875rem 2rem',
        borderRadius: '30px',
        textTransform: 'none',
        marginTop: '1.5rem',
        '&:hover': {
            backgroundColor: '#4A3880'
        }
    },
    emailText: {
        fontFamily: "'Montserrat', sans-serif",
        fontSize: '1.1rem',
        color: '#6750A4',
        fontWeight: 600,
        marginTop: '1rem'
    }
};

const faqs = [
    {
        question: "How do I create an account?",
        answer: "To create an account, click on the 'Register' button in the top navigation bar. Fill in your details including your name, email address, and password. Once submitted, you'll receive a confirmation email to verify your account."
    },
    {
        question: "How do I write and publish a post?",
        answer: "After logging in, click on 'Write' in the navigation menu. You'll be taken to the post editor where you can add a title, subtitle, and write your content using our rich text editor. You can also add a thumbnail image and select relevant categories. Once you're happy with your post, click 'Publish' to share it with the community."
    },
    {
        question: "How do I edit or delete my post?",
        answer: "Navigate to your profile by clicking on your profile icon and selecting 'My Profile'. Find the post you want to edit or delete, and click on the three-dot menu icon on the post card. Select 'Edit' to modify your post or 'Delete' to remove it permanently."
    },
    {
        question: "How do I follow other writers?",
        answer: "You can follow other writers by visiting their profile and clicking the 'Follow' button. You can also follow writers directly from the 'Popular Writers' section on the homepage. Posts from writers you follow will appear first in your feed when you have 'Following First' enabled."
    },
    {
        question: "How do I comment on a post?",
        answer: "Open any post by clicking on it, then scroll down to the comments section. If you're logged in, you'll see a text box where you can write your comment. Click 'Submit' to post your comment. You can also react to other users' comments using the reaction buttons."
    },
    {
        question: "How do I change my profile settings?",
        answer: "Click on your profile icon in the top navigation and select 'Settings'. From there, you can update your profile information, change your password, manage notification preferences, and customize your account settings."
    },
    {
        question: "What are the community guidelines?",
        answer: "We expect all users to be respectful and constructive in their interactions. Posts and comments should not contain hate speech, harassment, spam, or inappropriate content. Violations of community guidelines may result in content removal or account suspension. You can report inappropriate content using the report button on any post or comment."
    },
    {
        question: "How do I report inappropriate content?",
        answer: "If you see content that violates our community guidelines, click the report icon (flag) on the post or comment. Select the reason for your report and provide any additional details. Our moderation team will review the report and take appropriate action."
    }
];

function Support() {
    const [expanded, setExpanded] = useState(false);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleEmailClick = () => {
        window.location.href = 'mailto:support@vividkids.org?subject=Support Request';
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <Typography style={styles.title}>
                    How can we help?
                </Typography>
                <Typography style={styles.subtitle}>
                    Find answers to common questions below or reach out to our support team directly.
                </Typography>
            </div>

            {/* FAQ Section */}
            <div style={styles.section}>
                <Typography style={styles.sectionTitle}>
                    <QuestionAnswerIcon sx={{ color: '#6750A4', fontSize: 28 }} />
                    Frequently Asked Questions
                </Typography>

                {faqs.map((faq, index) => (
                    <Accordion
                        key={index}
                        expanded={expanded === `panel${index}`}
                        onChange={handleChange(`panel${index}`)}
                        sx={styles.accordion}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: '#6750A4' }} />}
                            sx={{
                                '& .MuiAccordionSummary-content': {
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 500,
                                    color: expanded === `panel${index}` ? '#6750A4' : '#1C1B1F'
                                }
                            }}
                        >
                            {faq.question}
                        </AccordionSummary>
                        <AccordionDetails sx={styles.accordionDetails}>
                            {faq.answer}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </div>

            {/* Contact Support Section */}
            <div style={styles.section}>
                <Typography style={styles.sectionTitle}>
                    <HelpOutlineIcon sx={{ color: '#6750A4', fontSize: 28 }} />
                    Still need help?
                </Typography>

                <Paper elevation={0} sx={styles.contactCard}>
                    <EmailIcon sx={{ fontSize: 48, color: '#6750A4' }} />
                    <Typography sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: '1.1rem',
                        color: '#1C1B1F',
                        marginTop: '1rem',
                        marginBottom: '0.5rem'
                    }}>
                        Contact our support team
                    </Typography>
                    <Typography sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: '0.95rem',
                        color: '#49454F'
                    }}>
                        We typically respond within 24-48 hours
                    </Typography>
                    <Typography style={styles.emailText}>
                        support@vividkids.org
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<EmailIcon />}
                        onClick={handleEmailClick}
                        sx={styles.emailButton}
                    >
                        Send us an email
                    </Button>
                </Paper>
            </div>
        </div>
    );
}

export default Support;
